import type { CSSProperties } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Cell, CellProps, Column, Row, useExpanded, useSortBy, UseExpandedRowProps } from 'react-table';
import styled from 'styled-components';
import { Bookmark, ClockRotateRight, Cube, Folder, Pin } from 'iconoir-react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import {
  Button,
  Drawer,
  DrawerPosition,
  Heading,
  Panel,
  Paragraph,
  Table,
} from '@savgroup-front-common/core';
import { ChevronDownIcon } from '@savgroup-front-common/core/src/protons/icons';
import { Tag } from '@savgroup-front-common/core/src/atoms/Tag';
import { toast } from '@savgroup-front-common/core/src/atoms/Toast';

import StockBulkActionDrawer from '../components/StockBulkActionDrawer';
import StockTableViews from '../components/StockTableViews';
import {
  cloneStockTableViewState,
  createEmptyStockFilters,
  type StockFilterKey,
  type StockTableViewState,
} from '../types/stockTableView.types';
import type {
  StockAddPartsConfirmPayload,
  StockInventoryBulkConfirmPayload,
} from '../types/stockBulk.types';
import { getSparePartCatalogEntry } from '../constants/sparePartsCatalog';
import {
  appendInventoriesFromStock,
  POC_INVENTORIES_FROM_STOCK_EVENT,
} from '../utils/stockLaunchedInventoryStorage';
import { useCurrentUserDisplayName } from '../hooks/useCurrentUserDisplayName';
import {
  applyStockEcartAdjustments,
  POC_INVENTORY_STOCK_IMPUTATION_EVENT,
} from '../utils/inventoryStockImputation';
import { buildStockPieceRowsForBulkAdd } from '../utils/stockAddParts';
import {
  addDaysToIsoDate,
  formatStockDate,
  formatStockDateTime,
  getLatestIsoDate,
  inventPocStockDates,
} from '../utils/stockDate.helpers';

interface StockPieceRow {
  referenceFournisseur: string;
  referenceConstructeur: string;
  constructeur: string;
  etatPiece: string;
  idElementStock: string;
  idPieceDetachee: string;
  referencePieceDetachee: string;
  nomPiece: string;
  fournisseur: string;
  idFournisseur: string;
  idStock: string;
  nomStock: string;
  siteAssocie: string;
  referenceDossier: string;
  idDossier: string;
  emplacementStock: string;
  statutElementStock: string;
  prixVenteTtc: string;
  prixAchatHt: string;
  /** Date d’entrée en stock (ISO 8601, POC). */
  dateEntreeStock: string;
  /** Date de dernière mise à jour (ISO 8601, POC). */
  dateDerniereMiseAJour: string;
}

interface StockGroupTableRow extends StockPieceRow {
  quantity: number;
  subRows?: StockGroupTableRow[];
  /** Nombre d’unités en statut « Disponible » (ligne agrégée par référence ; `null` sur la ligne parente stock). */
  quantiteDisponible: number | null;
  /** Somme des prix d’achat HT sur les unités concernées. */
  sommePrixAchatHt: number;
  /** Somme des prix de vente TTC sur les unités concernées. */
  sommePrixVenteTtc: number;
}

type StockTableRow = Row<StockGroupTableRow> & UseExpandedRowProps<StockGroupTableRow>;

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface StockDetailDrawerHeader {
  nomStock: string;
  /** `null` = vue de l’ensemble des pièces du stock (toutes références). */
  referencePieceDetachee: string | null;
}

/** Libellés POC pour statuts « anomalie » (réf. codes API : brokenDuringIntervention, brokenUponReceipt, defective, missing). */
const STOCK_STATUT_ANOMALIE = {
  casseeIntervention: "Cassée pendant l'intervention",
  casseeReception: 'Cassée à réception',
  defectueuse: 'Défectueuse',
  manquante: 'Manquante',
} as const;

const STOCK_MOCK_DATA_BASE: StockPieceRow[] = [
  {
    referenceFournisseur: 'HLL27196',
    referenceConstructeur: 'NIN-HLL27196',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e031b-c80a-42d6-8d87-891df66a8a54',
    idPieceDetachee: '0c9870ed-3c48-4487-80e9-8d3933cb5c53',
    referencePieceDetachee: 'HLL27196',
    nomPiece: 'Joy-Con (R) Rouge néon',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1086-1583',
    idDossier: 'd748d985-09de-4a26-9a7b-955f7075ccaa',
    emplacementStock: '-',
    statutElementStock: 'Réservé',
    prixVenteTtc: '101,74 EUR',
    prixAchatHt: '56,52 EUR',
  },
  {
    referenceFournisseur: 'FMS127857796',
    referenceConstructeur: 'NIN-FMS127857796',
    constructeur: 'Nintendo',
    etatPiece: 'Reconditionnée',
    idElementStock: '019e031c-bcad-4328-aab5-fa92beefceec',
    idPieceDetachee: 'affbf3d9-5bcb-478f-b8f9-6eaae12c6788',
    referencePieceDetachee: 'FMS127857796',
    nomPiece: 'Carte mère Nintendo Switch OLED',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1084-6607',
    idDossier: '32b8dfc0-0ea8-475e-af60-a8cb848a8320',
    emplacementStock: '-',
    statutElementStock: 'Réservé',
    prixVenteTtc: '37,46 EUR',
    prixAchatHt: '15,61 EUR',
  },
  {
    referenceFournisseur: 'FMS131099915',
    referenceConstructeur: 'NIN-FMS131099915',
    constructeur: 'Nintendo',
    etatPiece: 'Occasion',
    idElementStock: '019e031c-471c-4bb9-9efd-9c251a73442d',
    idPieceDetachee: '3102e54f-ea6a-48d8-a935-82fe97114b11',
    referencePieceDetachee: 'FMS131099915',
    nomPiece: 'Écran LCD Switch Lite 5,5"',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1084-6607',
    idDossier: '32b8dfc0-0ea8-475e-af60-a8cb848a8320',
    emplacementStock: '-',
    statutElementStock: 'Réservé',
    prixVenteTtc: '83,29 EUR',
    prixAchatHt: '46,27 EUR',
  },
  {
    referenceFournisseur: 'HLL27196-B',
    referenceConstructeur: 'NIN-HLL27196',
    constructeur: 'Nintendo',
    etatPiece: 'Reconditionnée',
    idElementStock: '019e040a-1111-4111-8111-111111111101',
    idPieceDetachee: '0c9870ed-3c48-4487-80e9-8d3933cb5c53',
    referencePieceDetachee: 'HLL27196',
    nomPiece: 'Joy-Con (R) Rouge néon',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'A1-R3',
    statutElementStock: 'Disponible',
    prixVenteTtc: '101,74 EUR',
    prixAchatHt: '56,52 EUR',
  },
  {
    referenceFournisseur: 'HLL27196-C',
    referenceConstructeur: 'NIN-HLL27196',
    constructeur: 'Nintendo',
    etatPiece: 'Occasion',
    idElementStock: '019e040a-1111-4111-8111-111111111102',
    idPieceDetachee: '0c9870ed-3c48-4487-80e9-8d3933cb5c53',
    referencePieceDetachee: 'HLL27196',
    nomPiece: 'Joy-Con (R) Rouge néon',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'A1-R4',
    statutElementStock: 'Disponible',
    prixVenteTtc: '101,74 EUR',
    prixAchatHt: '56,52 EUR',
  },
  {
    referenceFournisseur: 'FMS127857796-B',
    referenceConstructeur: 'NIN-FMS127857796',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e040a-2222-4222-8222-222222222201',
    idPieceDetachee: 'affbf3d9-5bcb-478f-b8f9-6eaae12c6788',
    referencePieceDetachee: 'FMS127857796',
    nomPiece: 'Carte mère Nintendo Switch OLED',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'B2-L1',
    statutElementStock: 'Disponible',
    prixVenteTtc: '37,46 EUR',
    prixAchatHt: '15,61 EUR',
  },
  {
    referenceFournisseur: 'FMS127857796-C',
    referenceConstructeur: 'NIN-FMS127857796',
    constructeur: 'Nintendo',
    etatPiece: 'Reconditionnée',
    idElementStock: '019e040a-2222-4222-8222-222222222202',
    idPieceDetachee: 'affbf3d9-5bcb-478f-b8f9-6eaae12c6788',
    referencePieceDetachee: 'FMS127857796',
    nomPiece: 'Carte mère Nintendo Switch OLED',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1085-9901',
    idDossier: '41c9eed0-1fb9-4c37-bf71-b9dc95994311',
    emplacementStock: 'B2-L2',
    statutElementStock: 'Réservé',
    prixVenteTtc: '37,46 EUR',
    prixAchatHt: '15,61 EUR',
  },
  {
    referenceFournisseur: 'FMS131099915-B',
    referenceConstructeur: 'NIN-FMS131099915',
    constructeur: 'Nintendo',
    etatPiece: 'Occasion',
    idElementStock: '019e040a-3333-4333-8333-333333333301',
    idPieceDetachee: '3102e54f-ea6a-48d8-a935-82fe97114b11',
    referencePieceDetachee: 'FMS131099915',
    nomPiece: 'Écran LCD Switch Lite 5,5"',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'C3-M1',
    statutElementStock: 'Disponible',
    prixVenteTtc: '83,29 EUR',
    prixAchatHt: '46,27 EUR',
  },
  {
    referenceFournisseur: 'FMS131099915-C',
    referenceConstructeur: 'NIN-FMS131099915',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e040a-3333-4333-8333-333333333302',
    idPieceDetachee: '3102e54f-ea6a-48d8-a935-82fe97114b11',
    referencePieceDetachee: 'FMS131099915',
    nomPiece: 'Écran LCD Switch Lite 5,5"',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'C3-M2',
    statutElementStock: 'En attente',
    prixVenteTtc: '83,29 EUR',
    prixAchatHt: '46,27 EUR',
  },
  {
    referenceFournisseur: 'SAM-88421',
    referenceConstructeur: 'SAM-88421-OEM',
    constructeur: 'Samsung',
    etatPiece: 'Reconditionnée',
    idElementStock: '019e040a-4444-4444-8444-444444444401',
    idPieceDetachee: 'a1000001-aaaa-bbbb-cccc-000000000001',
    referencePieceDetachee: 'SAM-88421',
    nomPiece: 'Coque arrière Galaxy S22',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'D1-P01',
    statutElementStock: 'Disponible',
    prixVenteTtc: '24,90 EUR',
    prixAchatHt: '12,40 EUR',
  },
  {
    referenceFournisseur: 'LG-BAT-2200',
    referenceConstructeur: 'LG-BAT-2200',
    constructeur: 'LG',
    etatPiece: 'Neuf',
    idElementStock: '019e040a-5555-4555-8555-555555555501',
    idPieceDetachee: 'b2000002-bbbb-cccc-dddd-000000000002',
    referencePieceDetachee: 'LG-BAT-2200',
    nomPiece: 'Batterie Li-ion 2200 mAh',
    fournisseur: 'PartsCo',
    idFournisseur: 'd2e895f4-1ab3-5a74-af5e-8b3b1a4b0568',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: 'ACH-2099-4401',
    idDossier: '52daffe1-2ac0-5d48-c882-c0ed060a05422',
    emplacementStock: 'D1-P02',
    statutElementStock: 'Réservé',
    prixVenteTtc: '19,99 EUR',
    prixAchatHt: '9,10 EUR',
  },
  {
    referenceFournisseur: 'LG-BAT-2200-B',
    referenceConstructeur: 'LG-BAT-2200',
    constructeur: 'LG',
    etatPiece: 'Occasion',
    idElementStock: '019e040a-5555-4555-8555-555555555502',
    idPieceDetachee: 'b2000002-bbbb-cccc-dddd-000000000002',
    referencePieceDetachee: 'LG-BAT-2200',
    nomPiece: 'Batterie Li-ion 2200 mAh',
    fournisseur: 'PartsCo',
    idFournisseur: 'd2e895f4-1ab3-5a74-af5e-8b3b1a4b0568',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'D1-P03',
    statutElementStock: 'Disponible',
    prixVenteTtc: '19,99 EUR',
    prixAchatHt: '9,10 EUR',
  },
  {
    referenceFournisseur: 'SONY-RC-500',
    referenceConstructeur: 'SONY-RC-500',
    constructeur: 'Sony',
    etatPiece: 'Reconditionnée',
    idElementStock: '019e040a-6666-4666-8666-666666666601',
    idPieceDetachee: 'c3000003-cccc-dddd-eeee-000000000003',
    referencePieceDetachee: 'SONY-RC-500',
    nomPiece: 'Télécommande universelle Bluetooth',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '525e6f9f-9922-5dbb-c916-482784f0b222',
    nomStock: 'Stock audio - Paris',
    siteAssocie: 'Auchan Aubagne',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'E4-S10',
    statutElementStock: 'Disponible',
    prixVenteTtc: '45,00 EUR',
    prixAchatHt: '22,50 EUR',
  },
  {
    referenceFournisseur: 'NIN-SCR-OLED-01',
    referenceConstructeur: 'NIN-SCR-OLED-01',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e0501-aaaa-4001-8001-000000000001',
    idPieceDetachee: 'e1111111-e111-4111-a111-111111111101',
    referencePieceDetachee: 'SCR-OLED-01',
    nomPiece: 'Écran OLED Switch (lot contrôle qualité)',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1087-2201',
    idDossier: 'f851e096-10ef-4b37-c88c-0660816ddbb1',
    emplacementStock: 'Q1-Z01',
    statutElementStock: STOCK_STATUT_ANOMALIE.defectueuse,
    prixVenteTtc: '89,90 EUR',
    prixAchatHt: '48,00 EUR',
  },
  {
    referenceFournisseur: 'NIN-SCR-OLED-01-B',
    referenceConstructeur: 'NIN-SCR-OLED-01',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e0501-aaaa-4001-8001-000000000002',
    idPieceDetachee: 'e1111111-e111-4111-a111-111111111101',
    referencePieceDetachee: 'SCR-OLED-01',
    nomPiece: 'Écran OLED Switch (lot contrôle qualité)',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1087-2201',
    idDossier: 'f851e096-10ef-4b37-c88c-0660816ddbb2',
    emplacementStock: 'Q1-Z02',
    statutElementStock: STOCK_STATUT_ANOMALIE.defectueuse,
    prixVenteTtc: '89,90 EUR',
    prixAchatHt: '48,00 EUR',
  },
  {
    referenceFournisseur: 'NIN-SCR-OLED-01-C',
    referenceConstructeur: 'NIN-SCR-OLED-01',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e0501-aaaa-4001-8001-000000000003',
    idPieceDetachee: 'e1111111-e111-4111-a111-111111111101',
    referencePieceDetachee: 'SCR-OLED-01',
    nomPiece: 'Écran OLED Switch (lot contrôle qualité)',
    fournisseur: 'ASWO',
    idFournisseur: 'a2b3c4d5-0001-4001-8001-000000000001',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1087-2202',
    idDossier: 'f851e096-10ef-4b37-c88c-0660816ddbb3',
    emplacementStock: 'Q1-Z03',
    statutElementStock: STOCK_STATUT_ANOMALIE.defectueuse,
    prixVenteTtc: '89,90 EUR',
    prixAchatHt: '47,20 EUR',
  },
  {
    referenceFournisseur: 'NIN-JC-L-GRIS-01',
    referenceConstructeur: 'NIN-JC-L-GRIS',
    constructeur: 'Nintendo',
    etatPiece: 'Occasion',
    idElementStock: '019e0502-bbbb-4002-8002-000000000001',
    idPieceDetachee: 'e2222222-e222-4222-a222-222222222202',
    referencePieceDetachee: 'JC-L-GRIS',
    nomPiece: 'Joy-Con (L) gris — retour atelier',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1087-3310',
    idDossier: 'a951f1a7-21f0-5c48-d99d-1771927eec01',
    emplacementStock: 'R2-K10',
    statutElementStock: STOCK_STATUT_ANOMALIE.casseeIntervention,
    prixVenteTtc: '38,00 EUR',
    prixAchatHt: '14,50 EUR',
  },
  {
    referenceFournisseur: 'NIN-JC-L-GRIS-02',
    referenceConstructeur: 'NIN-JC-L-GRIS',
    constructeur: 'Nintendo',
    etatPiece: 'Occasion',
    idElementStock: '019e0502-bbbb-4002-8002-000000000002',
    idPieceDetachee: 'e2222222-e222-4222-a222-222222222202',
    referencePieceDetachee: 'JC-L-GRIS',
    nomPiece: 'Joy-Con (L) gris — retour atelier',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1087-3310',
    idDossier: 'a951f1a7-21f0-5c48-d99d-1771927eec02',
    emplacementStock: 'R2-K11',
    statutElementStock: STOCK_STATUT_ANOMALIE.casseeIntervention,
    prixVenteTtc: '38,00 EUR',
    prixAchatHt: '14,50 EUR',
  },
  {
    referenceFournisseur: 'NIN-DOCK-V2-RET',
    referenceConstructeur: 'NIN-DOCK-V2',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e0503-cccc-4003-8003-000000000001',
    idPieceDetachee: 'e3333333-e333-4333-a333-333333333303',
    referencePieceDetachee: 'DOCK-V2',
    nomPiece: 'Station d’accueil Switch v2',
    fournisseur: 'SDS',
    idFournisseur: 'a2b3c4d5-0002-4002-8002-000000000002',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1087-4400',
    idDossier: 'b06202b8-32a1-6d59-e00e-2882038ffd01',
    emplacementStock: 'S3-L05',
    statutElementStock: STOCK_STATUT_ANOMALIE.casseeReception,
    prixVenteTtc: '54,90 EUR',
    prixAchatHt: '28,90 EUR',
  },
  {
    referenceFournisseur: 'NIN-FAN-INT-01',
    referenceConstructeur: 'NIN-FAN-INT',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e0504-dddd-4004-8004-000000000001',
    idPieceDetachee: 'e4444444-e444-4444-a444-444444444404',
    referencePieceDetachee: 'FAN-INT',
    nomPiece: 'Ventilateur interne Switch',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'T4-M01',
    statutElementStock: STOCK_STATUT_ANOMALIE.manquante,
    prixVenteTtc: '12,90 EUR',
    prixAchatHt: '5,40 EUR',
  },
  {
    referenceFournisseur: 'NIN-FAN-INT-02',
    referenceConstructeur: 'NIN-FAN-INT',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e0504-dddd-4004-8004-000000000002',
    idPieceDetachee: 'e4444444-e444-4444-a444-444444444404',
    referencePieceDetachee: 'FAN-INT',
    nomPiece: 'Ventilateur interne Switch',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1087-5501',
    idDossier: 'c17313c9-43b2-7e6a-f11f-3993149a0e01',
    emplacementStock: 'T4-M02',
    statutElementStock: STOCK_STATUT_ANOMALIE.manquante,
    prixVenteTtc: '12,90 EUR',
    prixAchatHt: '5,40 EUR',
  },
  {
    referenceFournisseur: 'SAM-USB-C-01',
    referenceConstructeur: 'SAM-USB-C',
    constructeur: 'Samsung',
    etatPiece: 'Neuf',
    idElementStock: '019e0505-eeee-4005-8005-000000000001',
    idPieceDetachee: 'e5555555-e555-4555-a555-555555555505',
    referencePieceDetachee: 'USB-C-MOD',
    nomPiece: 'Module port USB-C (Galaxy S21)',
    fournisseur: 'PartsCo',
    idFournisseur: 'd2e895f4-1ab3-5a74-af5e-8b3b1a4b0568',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: 'ACH-2099-5510',
    idDossier: 'd28424da-54c3-8f7b-0220-4aa425ab1f01',
    emplacementStock: 'D2-P20',
    statutElementStock: STOCK_STATUT_ANOMALIE.casseeReception,
    prixVenteTtc: '18,50 EUR',
    prixAchatHt: '8,20 EUR',
  },
  {
    referenceFournisseur: 'SAM-USB-C-02',
    referenceConstructeur: 'SAM-USB-C',
    constructeur: 'Samsung',
    etatPiece: 'Reconditionnée',
    idElementStock: '019e0505-eeee-4005-8005-000000000002',
    idPieceDetachee: 'e5555555-e555-4555-a555-555555555505',
    referencePieceDetachee: 'USB-C-MOD',
    nomPiece: 'Module port USB-C (Galaxy S21)',
    fournisseur: 'PartsCo',
    idFournisseur: 'd2e895f4-1ab3-5a74-af5e-8b3b1a4b0568',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: 'ACH-2099-5510',
    idDossier: 'd28424da-54c3-8f7b-0220-4aa425ab1f02',
    emplacementStock: 'D2-P21',
    statutElementStock: STOCK_STATUT_ANOMALIE.defectueuse,
    prixVenteTtc: '18,50 EUR',
    prixAchatHt: '8,20 EUR',
  },
  {
    referenceFournisseur: 'SAM-USB-C-03',
    referenceConstructeur: 'SAM-USB-C',
    constructeur: 'Samsung',
    etatPiece: 'Neuf',
    idElementStock: '019e0505-eeee-4005-8005-000000000003',
    idPieceDetachee: 'e5555555-e555-4555-a555-555555555505',
    referencePieceDetachee: 'USB-C-MOD',
    nomPiece: 'Module port USB-C (Galaxy S21)',
    fournisseur: 'PartsCo',
    idFournisseur: 'd2e895f4-1ab3-5a74-af5e-8b3b1a4b0568',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: 'ACH-2099-5511',
    idDossier: 'd28424da-54c3-8f7b-0220-4aa425ab1f03',
    emplacementStock: 'D2-P22',
    statutElementStock: STOCK_STATUT_ANOMALIE.casseeIntervention,
    prixVenteTtc: '18,50 EUR',
    prixAchatHt: '8,20 EUR',
  },
  {
    referenceFournisseur: 'LG-CAM-FRONT-01',
    referenceConstructeur: 'LG-CAM-FRONT',
    constructeur: 'LG',
    etatPiece: 'Neuf',
    idElementStock: '019e0506-ffff-4006-8006-000000000001',
    idPieceDetachee: 'e6666666-e666-4666-a666-666666666606',
    referencePieceDetachee: 'CAM-FRONT',
    nomPiece: 'Caméra avant (G8)',
    fournisseur: 'PartsCo',
    idFournisseur: 'd2e895f4-1ab3-5a74-af5e-8b3b1a4b0568',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'D3-P30',
    statutElementStock: STOCK_STATUT_ANOMALIE.manquante,
    prixVenteTtc: '22,00 EUR',
    prixAchatHt: '10,50 EUR',
  },
  {
    referenceFournisseur: 'LG-CAM-FRONT-02',
    referenceConstructeur: 'LG-CAM-FRONT',
    constructeur: 'LG',
    etatPiece: 'Neuf',
    idElementStock: '019e0506-ffff-4006-8006-000000000002',
    idPieceDetachee: 'e6666666-e666-4666-a666-666666666606',
    referencePieceDetachee: 'CAM-FRONT',
    nomPiece: 'Caméra avant (G8)',
    fournisseur: 'PartsCo',
    idFournisseur: 'd2e895f4-1ab3-5a74-af5e-8b3b1a4b0568',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: 'ACH-2099-6600',
    idDossier: 'e39535eb-65d4-9e8c-1331-5bb536bc2f12',
    emplacementStock: 'D3-P31',
    statutElementStock: STOCK_STATUT_ANOMALIE.manquante,
    prixVenteTtc: '22,00 EUR',
    prixAchatHt: '10,50 EUR',
  },
  {
    referenceFournisseur: 'LG-CAM-FRONT-03',
    referenceConstructeur: 'LG-CAM-FRONT',
    constructeur: 'LG',
    etatPiece: 'Neuf',
    idElementStock: '019e0506-ffff-4006-8006-000000000003',
    idPieceDetachee: 'e6666666-e666-4666-a666-666666666606',
    referencePieceDetachee: 'CAM-FRONT',
    nomPiece: 'Caméra avant (G8)',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '414d5f8e-8811-4caa-b805-371673e9a111',
    nomStock: 'Stock pièces téléphonie - Lyon',
    siteAssocie: 'Lyon - Plateforme téléphonie',
    referenceDossier: 'ACH-2099-6601',
    idDossier: 'e39535eb-65d4-9e8c-1331-5bb536bc2f13',
    emplacementStock: 'D3-P32',
    statutElementStock: STOCK_STATUT_ANOMALIE.defectueuse,
    prixVenteTtc: '22,00 EUR',
    prixAchatHt: '10,50 EUR',
  },
  {
    referenceFournisseur: 'SONY-RC-IR-01',
    referenceConstructeur: 'SONY-RC-IR',
    constructeur: 'Sony',
    etatPiece: 'Neuf',
    idElementStock: '019e0507-aaaa-4007-8007-000000000001',
    idPieceDetachee: 'e7777777-e777-4777-a777-777777777707',
    referencePieceDetachee: 'RC-IR-BLOCK',
    nomPiece: 'Bloc infrarouge télécommande série HT',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '525e6f9f-9922-5dbb-c916-482784f0b222',
    nomStock: 'Stock audio - Paris',
    siteAssocie: 'Auchan Aubagne',
    referenceDossier: 'ACH-3100-7701',
    idDossier: 'f4a646fc-76e5-a09d-2442-6cc647cd3a23',
    emplacementStock: 'E5-S20',
    statutElementStock: STOCK_STATUT_ANOMALIE.casseeIntervention,
    prixVenteTtc: '15,90 EUR',
    prixAchatHt: '7,80 EUR',
  },
  {
    referenceFournisseur: 'SONY-RC-IR-02',
    referenceConstructeur: 'SONY-RC-IR',
    constructeur: 'Sony',
    etatPiece: 'Occasion',
    idElementStock: '019e0507-aaaa-4007-8007-000000000002',
    idPieceDetachee: 'e7777777-e777-4777-a777-777777777707',
    referencePieceDetachee: 'RC-IR-BLOCK',
    nomPiece: 'Bloc infrarouge télécommande série HT',
    fournisseur: 'Eurosav',
    idFournisseur: 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457',
    idStock: '525e6f9f-9922-5dbb-c916-482784f0b222',
    nomStock: 'Stock audio - Paris',
    siteAssocie: 'Auchan Aubagne',
    referenceDossier: 'ACH-3100-7701',
    idDossier: 'f4a646fc-76e5-a09d-2442-6cc647cd3a24',
    emplacementStock: 'E5-S21',
    statutElementStock: STOCK_STATUT_ANOMALIE.casseeReception,
    prixVenteTtc: '15,90 EUR',
    prixAchatHt: '7,80 EUR',
  },
  {
    referenceFournisseur: 'SONY-AMP-FUSE-01',
    referenceConstructeur: 'SONY-AMP-FUSE',
    constructeur: 'Sony',
    etatPiece: 'Neuf',
    idElementStock: '019e0508-bbbb-4008-8008-000000000001',
    idPieceDetachee: 'e8888888-e888-4888-a888-888888888808',
    referencePieceDetachee: 'AMP-FUSE',
    nomPiece: 'Fusible ampli home-cinéma',
    fournisseur: 'ASWO',
    idFournisseur: 'a2b3c4d5-0001-4001-8001-000000000001',
    idStock: '525e6f9f-9922-5dbb-c916-482784f0b222',
    nomStock: 'Stock audio - Paris',
    siteAssocie: 'Auchan Aubagne',
    referenceDossier: '-',
    idDossier: '-',
    emplacementStock: 'E6-S30',
    statutElementStock: STOCK_STATUT_ANOMALIE.defectueuse,
    prixVenteTtc: '6,50 EUR',
    prixAchatHt: '2,10 EUR',
  },
  {
    referenceFournisseur: 'SONY-AMP-FUSE-02',
    referenceConstructeur: 'SONY-AMP-FUSE',
    constructeur: 'Sony',
    etatPiece: 'Neuf',
    idElementStock: '019e0508-bbbb-4008-8008-000000000002',
    idPieceDetachee: 'e8888888-e888-4888-a888-888888888808',
    referencePieceDetachee: 'AMP-FUSE',
    nomPiece: 'Fusible ampli home-cinéma',
    fournisseur: 'ASWO',
    idFournisseur: 'a2b3c4d5-0001-4001-8001-000000000001',
    idStock: '525e6f9f-9922-5dbb-c916-482784f0b222',
    nomStock: 'Stock audio - Paris',
    siteAssocie: 'Auchan Aubagne',
    referenceDossier: 'ACH-3100-8800',
    idDossier: '05b7570d-87f6-b1ae-3553-7dd758de4b34',
    emplacementStock: 'E6-S31',
    statutElementStock: STOCK_STATUT_ANOMALIE.defectueuse,
    prixVenteTtc: '6,50 EUR',
    prixAchatHt: '2,10 EUR',
  },
  {
    referenceFournisseur: 'SONY-AMP-FUSE-03',
    referenceConstructeur: 'SONY-AMP-FUSE',
    constructeur: 'Sony',
    etatPiece: 'Neuf',
    idElementStock: '019e0508-bbbb-4008-8008-000000000003',
    idPieceDetachee: 'e8888888-e888-4888-a888-888888888808',
    referencePieceDetachee: 'AMP-FUSE',
    nomPiece: 'Fusible ampli home-cinéma',
    fournisseur: 'ASWO',
    idFournisseur: 'a2b3c4d5-0001-4001-8001-000000000001',
    idStock: '525e6f9f-9922-5dbb-c916-482784f0b222',
    nomStock: 'Stock audio - Paris',
    siteAssocie: 'Auchan Aubagne',
    referenceDossier: 'ACH-3100-8801',
    idDossier: '05b7570d-87f6-b1ae-3553-7dd758de4b35',
    emplacementStock: 'E6-S32',
    statutElementStock: STOCK_STATUT_ANOMALIE.defectueuse,
    prixVenteTtc: '6,50 EUR',
    prixAchatHt: '2,10 EUR',
  },
  {
    referenceFournisseur: 'NIN-PWR-PCB-01',
    referenceConstructeur: 'NIN-PWR-PCB',
    constructeur: 'Nintendo',
    etatPiece: 'Neuf',
    idElementStock: '019e0509-cccc-4009-8009-000000000001',
    idPieceDetachee: 'e9999999-e999-4999-a999-999999999909',
    referencePieceDetachee: 'PWR-PCB',
    nomPiece: 'Carte alimentation Switch Lite',
    fournisseur: 'SDS',
    idFournisseur: 'a2b3c4d5-0002-4002-8002-000000000002',
    idStock: '323c4f7d-7512-4b92-a694-269562d8f050',
    nomStock: 'Stock NINTENDO - SAV constructeur (Auchan)',
    siteAssocie: 'Auchan Trappes',
    referenceDossier: 'ACH-1087-9900',
    idDossier: '16c8681e-98a7-c2bf-4664-8ee869ef5c45',
    emplacementStock: 'U7-N01',
    statutElementStock: STOCK_STATUT_ANOMALIE.casseeReception,
    prixVenteTtc: '29,90 EUR',
    prixAchatHt: '13,40 EUR',
  },
];

const stablePieceDetacheeIdFromReference = (reference: string): string => {
  const hex = [...reference]
    .map((ch) => ch.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('')
    .padEnd(32, '0')
    .slice(0, 32);

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(12, 15)}-a${hex.slice(15, 18)}-${hex.slice(18, 30)}`;
};

/** Clé de regroupement / détail : la réf. pièce détachée (stable entre catalogue et mock). */
const getStockPieceGroupKey = (
  row: Pick<StockPieceRow, 'referencePieceDetachee' | 'idPieceDetachee'>,
): string =>
  row.referencePieceDetachee && row.referencePieceDetachee !== '-'
    ? row.referencePieceDetachee
    : row.idPieceDetachee;

/** Échantillon d’états pièce (équivalent API : Neuf / Reconditioned / Used) pour les hits mock. */
const STOCK_API_ETATS_PIECE_EXEMPLES = ['Neuf', 'Reconditionnée', 'Occasion', 'Neuf'] as const;

/** Statuts élément stock sur les hits API (dont anomalies). */
const STOCK_API_STATUTS_ELEMENT_EXEMPLES = [
  'Réservé',
  STOCK_STATUT_ANOMALIE.defectueuse,
  STOCK_STATUT_ANOMALIE.casseeReception,
  STOCK_STATUT_ANOMALIE.casseeIntervention,
] as const;

/** Jeu issu d’une réponse API (hits) — mappé en lignes `StockPieceRow` pour le POC (sans statut Consumed : hors stock). */
const STOCK_API_HIT_ROWS: StockPieceRow[] = (
  [
    {
      stockItemId: '019e168f-b0fa-4b94-9499-5590a32cb270',
      stockItemStatus: 'Booked',
      sparePartReference: 'FMC81373942',
      stockName: 'Stock NINTENDO - SAV constructeur (Auchan)',
      fileReference: 'ACH-1086-8249',
      supplierName: 'Eurosav',
      stockInputTimeUtc: '2026-05-11T10:22:56.25Z',
    },
    {
      stockItemId: '019e168c-5c63-4ce9-ac14-274cd3ecb2d0',
      stockItemStatus: 'Booked',
      sparePartReference: '9291885',
      stockName: 'Stock NINTENDO - SAV constructeur (Auchan)',
      fileReference: 'ACH-1085-7428',
      supplierName: 'ASWO',
      stockInputTimeUtc: '2026-05-11T10:19:17.987Z',
    },
    {
      stockItemId: '019e168c-389c-4b4c-92a6-dac7cb8bd1da',
      stockItemStatus: 'Booked',
      sparePartReference: 'M492644',
      stockName: 'Stock NINTENDO - SAV constructeur (Auchan)',
      fileReference: 'ACH-1085-7428',
      supplierName: 'ASWO',
      stockInputTimeUtc: '2026-05-11T10:19:08.828Z',
    },
    {
      stockItemId: '019e1665-48ad-4cb3-a296-dcc35c89cf38',
      stockItemStatus: 'Booked',
      sparePartReference: 'FM82836923',
      stockName: 'Stock NINTENDO - SAV constructeur (Auchan)',
      fileReference: 'ACH-1086-0114',
      supplierName: 'Eurosav',
      stockInputTimeUtc: '2026-05-11T09:36:37.037Z',
    },
  ] as const
).map((hit, index) => {
  const ref = hit.sparePartReference;
  const statutElementStock = STOCK_API_STATUTS_ELEMENT_EXEMPLES[index % STOCK_API_STATUTS_ELEMENT_EXEMPLES.length];
  const idFournisseurEurosav = 'c1f784e3-09a2-4f63-9e4d-7a2a0f3a9457';
  const idFournisseurAswo = 'a2b3c4d5-0001-4001-8001-000000000001';
  const idFournisseur = hit.supplierName === 'ASWO' ? idFournisseurAswo : idFournisseurEurosav;
  const idStockNintendoAuchan = '323c4f7d-7512-4b92-a694-269562d8f050';
  const idPieceDetachee = stablePieceDetacheeIdFromReference(ref);
  const dossierHex = (100000000000 + index).toString(16).padStart(12, '0');
  const etatPiece = STOCK_API_ETATS_PIECE_EXEMPLES[index % STOCK_API_ETATS_PIECE_EXEMPLES.length];

  return {
    referenceFournisseur: ref,
    referenceConstructeur: `NIN-${ref}`,
    constructeur: 'Nintendo',
    etatPiece,
    idElementStock: hit.stockItemId,
    idPieceDetachee,
    referencePieceDetachee: ref,
    nomPiece: `Pièce ${ref}`,
    fournisseur: hit.supplierName,
    idFournisseur,
    idStock: idStockNintendoAuchan,
    nomStock: hit.stockName,
    siteAssocie: 'Auchan Trappes',
    referenceDossier: hit.fileReference,
    idDossier: `01e0d000-0000-4000-8000-${dossierHex}`,
    emplacementStock: '-',
    statutElementStock,
    prixVenteTtc: '-',
    prixAchatHt: '-',
    dateEntreeStock: hit.stockInputTimeUtc,
    dateDerniereMiseAJour: addDaysToIsoDate(hit.stockInputTimeUtc, (index % 5) + 1),
  };
});

const enrichStockPieceRowWithPocDates = (row: StockPieceRow, index: number): StockPieceRow => {
  if (row.dateEntreeStock && row.dateDerniereMiseAJour) {
    return row;
  }

  const seed = index + row.idElementStock.length;

  return {
    ...row,
    ...inventPocStockDates(seed),
  };
};

const STOCK_MOCK_DATA: StockPieceRow[] = [...STOCK_MOCK_DATA_BASE, ...STOCK_API_HIT_ROWS].map(
  enrichStockPieceRowWithPocDates,
);

const STOCK_AGG_PARENT_ID_PREFIX = 'aggregated-stock-';

/** À repasser à `true` si le bouton « Voir » sur la ligne parente (regroupement stock) doit réapparaître. */
const SHOW_VIEW_BUTTON_ON_STOCK_PARENT_ROW = false;

const parseEuroAmountFromStockCell = (raw: string): number => {
  if (!raw || raw === '-') {
    return 0;
  }

  const normalized = raw
    .replace(/\s*EUR\s*$/i, '')
    .trim()
    .replace(/\s/g, '')
    .replace(',', '.');

  const value = Number.parseFloat(normalized);

  return Number.isFinite(value) ? value : 0;
};

const formatSumEuro = (value: number): string =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const aggregateQuantiteDisponibleAndPriceSums = (
  units: StockPieceRow[],
): Pick<StockGroupTableRow, 'quantiteDisponible' | 'sommePrixAchatHt' | 'sommePrixVenteTtc'> => {
  let sommePrixAchatHt = 0;
  let sommePrixVenteTtc = 0;
  let quantiteDisponible = 0;

  for (const unit of units) {
    sommePrixAchatHt += parseEuroAmountFromStockCell(unit.prixAchatHt);
    sommePrixVenteTtc += parseEuroAmountFromStockCell(unit.prixVenteTtc);

    if (unit.statutElementStock === 'Disponible') {
      quantiteDisponible += 1;
    }
  }

  return { quantiteDisponible, sommePrixAchatHt, sommePrixVenteTtc };
};

/**
 * Une ligne parente par stock ; enfants = une ligne par référence de pièce (idPieceDetachee) avec quantité agrégée.
 * Même pour un stock à une seule ligne physique : parent synthétique + un enfant (même détail qu’après dépliage).
 */
const groupStockPiecesByStockForTable = (items: StockPieceRow[]): StockGroupTableRow[] => {
  const byStock = new Map<string, StockPieceRow[]>();

  for (const item of items) {
    const bucket = byStock.get(item.idStock);

    if (bucket) {
      bucket.push(item);
    } else {
      byStock.set(item.idStock, [item]);
    }
  }

  const parents: StockGroupTableRow[] = [];

  for (const stockRows of byStock.values()) {
    const sortedLines = [...stockRows].sort((a, b) => {
      const byNomPiece = a.nomPiece.localeCompare(b.nomPiece, 'fr', { sensitivity: 'base' });

      if (byNomPiece !== 0) {
        return byNomPiece;
      }

      return a.referencePieceDetachee.localeCompare(b.referencePieceDetachee);
    });
    const firstLine = sortedLines[0];

    const byPiece = new Map<string, StockPieceRow[]>();

    for (const line of sortedLines) {
      const pieceKey = getStockPieceGroupKey(line);
      const pieceBucket = byPiece.get(pieceKey);

      if (pieceBucket) {
        pieceBucket.push(line);
      } else {
        byPiece.set(pieceKey, [line]);
      }
    }

    const pieceIdsSorted = [...byPiece.keys()].sort((idA, idB) => {
      const rowA = byPiece.get(idA)![0];
      const rowB = byPiece.get(idB)![0];
      const byNomPiece = rowA.nomPiece.localeCompare(rowB.nomPiece, 'fr', { sensitivity: 'base' });

      if (byNomPiece !== 0) {
        return byNomPiece;
      }

      return rowA.referencePieceDetachee.localeCompare(rowB.referencePieceDetachee);
    });

    const subRows: StockGroupTableRow[] = pieceIdsSorted.map((pieceId) => {
      const unitRows = byPiece.get(pieceId)!;
      const sortedUnits = [...unitRows].sort((a, b) =>
        a.referenceFournisseur.localeCompare(b.referenceFournisseur),
      );
      const representative = sortedUnits[0];
      const aggregates = aggregateQuantiteDisponibleAndPriceSums(sortedUnits);

      return {
        ...representative,
        quantity: sortedUnits.length,
        quantiteDisponible: aggregates.quantiteDisponible,
        sommePrixAchatHt: aggregates.sommePrixAchatHt,
        sommePrixVenteTtc: aggregates.sommePrixVenteTtc,
        dateDerniereMiseAJour: getLatestIsoDate(
          sortedUnits.map((unit) => unit.dateDerniereMiseAJour),
        ),
      };
    });

    const stockAggregates = aggregateQuantiteDisponibleAndPriceSums(sortedLines);

    parents.push({
      ...firstLine,
      idElementStock: `${STOCK_AGG_PARENT_ID_PREFIX}${firstLine.idStock}`,
      idPieceDetachee: `parent-stock-${firstLine.idStock}`,
      referenceFournisseur: '-',
      referenceConstructeur: '-',
      referencePieceDetachee: '-',
      nomPiece: '-',
      constructeur: '-',
      fournisseur: '-',
      etatPiece: '-',
      emplacementStock: '-',
      referenceDossier: '-',
      idDossier: '-',
      statutElementStock: '-',
      prixVenteTtc: '-',
      prixAchatHt: '-',
      dateEntreeStock: '-',
      dateDerniereMiseAJour: getLatestIsoDate(
        sortedLines.map((line) => line.dateDerniereMiseAJour),
      ),
      nomStock: firstLine.nomStock,
      idStock: firstLine.idStock,
      quantity: sortedLines.length,
      quantiteDisponible: null,
      sommePrixAchatHt: stockAggregates.sommePrixAchatHt,
      sommePrixVenteTtc: stockAggregates.sommePrixVenteTtc,
      subRows,
    });
  }

  return parents.sort((a, b) => a.nomStock.localeCompare(b.nomStock));
};

const getDistinctStockNames = (items: StockPieceRow[]): string[] =>
  Array.from(new Set(items.map((item) => item.nomStock))).sort((a, b) =>
    a.localeCompare(b, 'fr', { sensitivity: 'base' }),
  );

const isNomStockFilterFullySelected = (selected: string[], allStockNames: string[]): boolean =>
  allStockNames.length === 0 ||
  (selected.length > 0 && allStockNames.every((name) => selected.includes(name)));

const FiltersContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const ActionsContainer = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SearchAndFiltersContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
  align-items: center;
  flex-wrap: wrap;
`;

/** Défilement horizontal lorsque le tableau est plus large que l’écran. */
const $StockTableScrollArea = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;

  [data-testid='stock-table'] thead,
  [data-testid='stock-table'] thead th {
    background-color: #eef2ff;
  }
`;

const SearchInputWrapper = styled.div`
  flex: 0 0 auto;
  min-width: 260px;
  flex-grow: 1;
  max-width: 360px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccd2f9;
  border-radius: 4px;
  font-size: 0.875rem;
  height: 32px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #5a72ff;
  }
`;

const FilterButtonStyled = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding: 7px 8px;
  background: ${({ theme }) => theme.systemColors?.defaultBackground || '#fff'};
  border: 1px solid ${({ $isActive }) => ($isActive ? '#a2abe9' : '#ccd2f9')};
  border-radius: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors?.mainTextColor || '#333'};
  cursor: pointer;
  height: 32px;
`;

const FilterIconWrapper = styled.div`
  margin-right: 0.3rem;
  display: flex;
  align-items: center;
`;

const FilterNumber = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 4px 1px;
  margin-left: 8px;
  background: #5369eb;
  border-radius: 4px;
  font-size: 10px;
  color: #fff;
`;

const FilterChevronWrapper = styled.div<{ $isMenuOpen?: boolean }>`
  display: flex;
  margin-left: 0.25rem;
  transition: transform 150ms cubic-bezier(0.47, 1.64, 0.41, 0.8);
  transform: rotate(${({ $isMenuOpen }) => ($isMenuOpen ? '180deg' : '0deg')});
`;

const FilterMenuDropdown = styled.div<{ $isOpen: boolean; $top: number; $left: number }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  background: white;
  border: 1px solid #ccd2f9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 220px;
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
`;

const FilterMenuItem = styled.label`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.newUI?.variationColors?.light5 || '#f5f5f5'};
  }

  input[type='checkbox'] {
    margin-right: 0.5rem;
  }
`;

const FilterMenuItemSelectAll = styled(FilterMenuItem)`
  font-weight: 600;
  margin-bottom: 0.25rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid #e2e8f0;
`;

const $ViewButton = styled.button`
  border: 1px solid #ccd2f9;
  background: #fff;
  border-radius: 4px;
  min-width: 56px;
  height: 28px;
  padding: 0 8px;
  cursor: pointer;
  color: #5c616b;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #5a72ff;
    color: #5a72ff;
    background: #f8f9ff;
  }
`;

const $DetailTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
  margin-top: 0.75rem;

  th,
  td {
    border: 1px solid #e2e8f0;
    padding: 0.45rem 0.55rem;
    text-align: left;
    vertical-align: middle;
  }

  th {
    background: #f8fafc;
    font-weight: 600;
    color: #334155;
  }

  tbody tr:nth-child(even) {
    background: #fafbfc;
  }
`;

const $ExpandCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
`;

const $ExpandToggle = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #5c616b;
  border-radius: 4px;

  &:hover {
    color: #5a72ff;
    background: #f0f2ff;
  }
`;

/** Colonnes fusionnées sur la ligne parente (bandeau stock + site, aligné à gauche). */
const STOCK_TABLE_PARENT_BAND_BODY_COLUMN_IDS = new Set<string>([
  'nomPiece',
  'referenceConstructeur',
  'constructeur',
  'referenceFournisseur',
  'fournisseur',
  'quantity',
]);

const STOCK_TABLE_PARENT_BAND_COLSPAN = STOCK_TABLE_PARENT_BAND_BODY_COLUMN_IDS.size;

const $StockParentBand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  line-height: 1.3;
  text-align: left;
`;

const $StockParentBandStockName = styled.span<{ $isChildRow?: boolean }>`
  font-size: ${({ $isChildRow }) => ($isChildRow ? '0.8125rem' : '12px')};
`;

const $StockParentBandSite = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: #4338ca;
`;

type StockTableCellPropsResult = {
  style: CSSProperties;
  colSpan?: number;
  'aria-hidden'?: boolean;
};

/** Mise en forme des cellules : ligne parente de groupe vs lignes enfants (dépliées). */
const getStockTableCellProps = (cell?: Cell<StockGroupTableRow, unknown>): StockTableCellPropsResult => {
  const style: CSSProperties = {
    verticalAlign: 'middle',
  };

  // Table.Header appelle aussi column.getProps() sans argument.
  if (!cell) {
    return { style };
  }

  const row = cell.row as StockTableRow;
  const colId = cell.column.id;
  const isChild = row.depth > 0;
  const isCollapsibleParent =
    row.depth === 0 && Boolean(row.original.subRows && row.original.subRows.length > 0);

  if (isCollapsibleParent && STOCK_TABLE_PARENT_BAND_BODY_COLUMN_IDS.has(colId) && colId !== 'nomPiece') {
    return {
      style: { display: 'none' },
      'aria-hidden': true,
    };
  }

  if (isChild) {
    style.background = '#f4f7fb';
    style.color = '#334155';
  } else if (isCollapsibleParent) {
    style.background = '#eef2ff';
    style.fontWeight = 600;
    style.color = '#1e1b4b';
  }

  if (isCollapsibleParent && colId === 'nomPiece') {
    style.textAlign = 'left';

    return {
      style,
      colSpan: STOCK_TABLE_PARENT_BAND_COLSPAN,
    };
  }

  return { style };
};

const buildStockDataWithImputations = (): StockPieceRow[] =>
  applyStockEcartAdjustments(STOCK_MOCK_DATA);

const StockPage = () => {
  const currentUserDisplayName = useCurrentUserDisplayName();
  const [stockData, setStockData] = useState<StockPieceRow[]>(buildStockDataWithImputations);
  const [searchQuery, setSearchQuery] = useState('');
  const [openFilterMenu, setOpenFilterMenu] = useState<StockFilterKey | null>(null);
  const [filterMenuPosition, setFilterMenuPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const filterMenuRef = useRef<HTMLDivElement | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<Record<StockFilterKey, string[]>>(() => {
    const filters = createEmptyStockFilters();
    filters.nomStock = getDistinctStockNames(buildStockDataWithImputations());

    return filters;
  });
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [detailDrawerUnits, setDetailDrawerUnits] = useState<StockPieceRow[]>([]);
  const [detailDrawerHeader, setDetailDrawerHeader] = useState<StockDetailDrawerHeader | null>(null);
  const [isStockBulkDrawerOpen, setIsStockBulkDrawerOpen] = useState(false);

  const allStockNames = useMemo(() => getDistinctStockNames(stockData), [stockData]);

  const isStockFilterFullySelected = useMemo(
    () => isNomStockFilterFullySelected(selectedFilters.nomStock, allStockNames),
    [allStockNames, selectedFilters.nomStock],
  );

  const isStockFilterPartiallySelected =
    selectedFilters.nomStock.length > 0 && !isStockFilterFullySelected;

  useEffect(() => {
    setSelectedFilters((prev) => {
      if (allStockNames.length === 0 || prev.nomStock.length === 0) {
        return prev;
      }

      const missing = allStockNames.filter((name) => !prev.nomStock.includes(name));

      if (missing.length === 0) {
        return prev;
      }

      const previousKnown = prev.nomStock.filter((name) => allStockNames.includes(name));
      const hadFullSelectionBefore =
        previousKnown.length === allStockNames.length - missing.length &&
        previousKnown.length === prev.nomStock.length;

      if (hadFullSelectionBefore) {
        return { ...prev, nomStock: allStockNames };
      }

      return prev;
    });
  }, [allStockNames]);

  const filterOptions = useMemo<Record<StockFilterKey, FilterOption[]>>(() => {
    const buildOptions = (key: StockFilterKey): FilterOption[] => {
      const values = stockData.map((item) => item[key]);

      return Array.from(new Set(values)).map((value) => ({
        value,
        label: value,
        count: stockData.filter((item) => item[key] === value).length,
      }));
    };

    return {
      siteAssocie: buildOptions('siteAssocie'),
      nomStock: buildOptions('nomStock'),
      fournisseur: buildOptions('fournisseur'),
      constructeur: buildOptions('constructeur'),
      statutElementStock: buildOptions('statutElementStock'),
      etatPiece: buildOptions('etatPiece'),
    };
  }, [stockData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setOpenFilterMenu(null);
      }
    };

    if (openFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterMenu]);

  useEffect(() => {
    const reloadStockFromImputations = () => {
      setStockData(buildStockDataWithImputations());
    };

    window.addEventListener(POC_INVENTORY_STOCK_IMPUTATION_EVENT, reloadStockFromImputations);

    return () => {
      window.removeEventListener(POC_INVENTORY_STOCK_IMPUTATION_EVENT, reloadStockFromImputations);
    };
  }, []);

  const handleApplyStockTableView = useCallback(
    (state: StockTableViewState) => {
      setSearchQuery(state.searchQuery);
      const cloned = cloneStockTableViewState(state);

      if (cloned.selectedFilters.nomStock.length === 0) {
        cloned.selectedFilters.nomStock = getDistinctStockNames(stockData);
      }

      setSelectedFilters(cloned.selectedFilters);
    },
    [stockData],
  );

  const handleStockFilterSelectAllToggle = useCallback(() => {
    setSelectedFilters((prev) => ({
      ...prev,
      nomStock: isNomStockFilterFullySelected(prev.nomStock, allStockNames) ? [] : [...allStockNames],
    }));
  }, [allStockNames]);

  const handleFilterToggle = (filterType: StockFilterKey, value: string) => {
    setSelectedFilters((prev) => {
      const current = prev[filterType];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      return { ...prev, [filterType]: next };
    });
  };

  const handleFilterButtonClick = (
    filterType: StockFilterKey,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const isOpen = openFilterMenu === filterType;

    if (isOpen) {
      setOpenFilterMenu(null);

      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();

    setFilterMenuPosition({ top: rect.bottom + 4, left: rect.left });
    setOpenFilterMenu(filterType);
  };

  const handleCloseDetailDrawer = useCallback(() => {
    setIsDetailDrawerOpen(false);
    setDetailDrawerUnits([]);
    setDetailDrawerHeader(null);
  }, []);

  const getFilterIcon = (filterType: StockFilterKey) => {
    const isActive =
      filterType === 'nomStock'
        ? isStockFilterPartiallySelected
        : selectedFilters[filterType].length > 0;
    const color = isActive || openFilterMenu === filterType ? '#5a72ff' : '#5c616b';

    switch (filterType) {
      case 'siteAssocie':
        return <Folder width="18px" height="18px" color={color} />;
      case 'nomStock':
        return <ClockRotateRight width="18px" height="18px" color={color} />;
      case 'fournisseur':
        return <Bookmark width="18px" height="18px" color={color} />;
      case 'constructeur':
        return <Cube width="18px" height="18px" color={color} />;
      case 'statutElementStock':
        return <Pin width="18px" height="18px" color={color} />;
      case 'etatPiece':
        return <Pin width="18px" height="18px" color={color} />;
      default:
        return null;
    }
  };

  const filteredStockData = useMemo(
    () =>
      stockData.filter((item) => {
        const matchesSite =
          selectedFilters.siteAssocie.length === 0 ||
          selectedFilters.siteAssocie.includes(item.siteAssocie);
        const matchesStockName =
          isNomStockFilterFullySelected(selectedFilters.nomStock, allStockNames) ||
          selectedFilters.nomStock.includes(item.nomStock);
        const matchesSupplier =
          selectedFilters.fournisseur.length === 0 ||
          selectedFilters.fournisseur.includes(item.fournisseur);
        const matchesConstructeur =
          selectedFilters.constructeur.length === 0 ||
          selectedFilters.constructeur.includes(item.constructeur);
        const matchesStatus =
          selectedFilters.statutElementStock.length === 0 ||
          selectedFilters.statutElementStock.includes(item.statutElementStock);
        const matchesEtat =
          selectedFilters.etatPiece.length === 0 ||
          selectedFilters.etatPiece.includes(item.etatPiece);

        const query = searchQuery.trim().toLowerCase();
        const matchesSearch =
          !query ||
          item.referenceFournisseur.toLowerCase().includes(query) ||
          item.referencePieceDetachee.toLowerCase().includes(query) ||
          item.fournisseur.toLowerCase().includes(query) ||
          item.referenceDossier.toLowerCase().includes(query) ||
          item.siteAssocie.toLowerCase().includes(query) ||
          item.nomPiece.toLowerCase().includes(query);

        return (
          matchesSite &&
          matchesStockName &&
          matchesSupplier &&
          matchesConstructeur &&
          matchesStatus &&
          matchesEtat &&
          matchesSearch
        );
      }),
    [allStockNames, searchQuery, selectedFilters, stockData],
  );

  const handleOpenDetail = useCallback(
    (row: StockGroupTableRow) => {
      const pieceGroupKey = getStockPieceGroupKey(row);
      const units = stockData
        .filter(
          (item) => item.idStock === row.idStock && getStockPieceGroupKey(item) === pieceGroupKey,
        )
        .sort((a, b) => a.referenceFournisseur.localeCompare(b.referenceFournisseur));

      setDetailDrawerUnits(units);
      setDetailDrawerHeader({
        nomStock: row.nomStock,
        referencePieceDetachee: row.referencePieceDetachee,
      });
      setIsDetailDrawerOpen(true);
    },
    [stockData],
  );

  const handleOpenStockDetail = useCallback(
    (row: StockGroupTableRow) => {
      const units = stockData
        .filter((item) => item.idStock === row.idStock)
        .sort((a, b) => {
          const byNomPiece = a.nomPiece.localeCompare(b.nomPiece, 'fr', { sensitivity: 'base' });

          if (byNomPiece !== 0) {
            return byNomPiece;
          }

          const byRefPiece = a.referencePieceDetachee.localeCompare(b.referencePieceDetachee);

          if (byRefPiece !== 0) {
            return byRefPiece;
          }

          return a.referenceFournisseur.localeCompare(b.referenceFournisseur);
        });

      setDetailDrawerUnits(units);
      setDetailDrawerHeader({
        nomStock: row.nomStock,
        referencePieceDetachee: null,
      });
      setIsDetailDrawerOpen(true);
    },
    [stockData],
  );

  const groupedFilteredData = useMemo(
    () => groupStockPiecesByStockForTable(filteredStockData),
    [filteredStockData],
  );

  const availableStocksForDrawer = useMemo(
    () =>
      groupStockPiecesByStockForTable(stockData).map((row) => ({
        idStock: row.idStock,
        nomStock: row.nomStock,
        siteAssocie: row.siteAssocie,
        nombreArticles: row.subRows?.length ?? 0,
        quantiteTheorique: row.quantity,
        sommePaHt: row.sommePrixAchatHt,
        sommePvTtc: row.sommePrixVenteTtc,
      })),
    [stockData],
  );

  const handleOpenStockBulkDrawer = useCallback(() => {
    setIsStockBulkDrawerOpen(true);
  }, []);

  const handleCloseStockBulkDrawer = useCallback(() => {
    setIsStockBulkDrawerOpen(false);
  }, []);

  const handleConfirmInventoryBulk = useCallback(
    ({ superviseur, items }: StockInventoryBulkConfirmPayload) => {
      appendInventoriesFromStock(items, superviseur, currentUserDisplayName);
      window.dispatchEvent(new Event(POC_INVENTORIES_FROM_STOCK_EVENT));
      const lines = items.map((s) => `• ${s.nomStock} (${s.siteAssocie})`).join(' — ');

      toast.success(
        <span>
          {items.length} inventaire(s) créé(s) au statut « Nouveau » (superviseur : {superviseur}). Ouvrez la
          page Inventaire pour les consulter.
          <br />
          <small style={{ opacity: 0.9 }}>{lines}</small>
        </span>,
        { autoClose: 5000, showIcon: true },
      );
    },
    [currentUserDisplayName],
  );

  const handleConfirmAddParts = useCallback(
    ({ lines, items }: StockAddPartsConfirmPayload) => {
      const newRows = lines.flatMap((line) => {
        const catalogEntry = getSparePartCatalogEntry(line.catalogPieceId);

        if (!catalogEntry) {
          return [];
        }

        return buildStockPieceRowsForBulkAdd({
          catalogEntry,
          stocks: items,
          quantity: line.quantity,
          etatPiece: line.etatPiece,
          emplacementStock: line.emplacementStock,
        });
      });

      if (newRows.length === 0) {
        toast.error('Aucune pièce valide à ajouter.', { autoClose: 4000, showIcon: true });

        return;
      }

      const expectedUnitCount = lines.reduce((sum, line) => {
        if (!getSparePartCatalogEntry(line.catalogPieceId)) {
          return sum;
        }

        return sum + line.quantity * items.length;
      }, 0);

      if (newRows.length < expectedUnitCount) {
        toast.error('Certaines pièces du catalogue sont introuvables.', {
          autoClose: 4000,
          showIcon: true,
        });

        return;
      }

      setStockData((prev) => [...prev, ...newRows]);

      const totalUnits = newRows.length;
      const stockLines = items.map((s) => `• ${s.nomStock}`).join(' — ');
      const pieceSummary = lines
        .map((line) => {
          const entry = getSparePartCatalogEntry(line.catalogPieceId);

          return entry ? `${entry.nomPiece} (×${line.quantity})` : null;
        })
        .filter((label): label is string => label !== null)
        .join(' — ');

      toast.success(
        <span>
          {totalUnits} unité(s) ajoutée(s) pour {lines.length} référence(s) de pièce.
          <br />
          <small style={{ opacity: 0.9 }}>
            {pieceSummary}
            <br />
            {stockLines}
          </small>
        </span>,
        { autoClose: 5000, showIcon: true },
      );
    },
    [],
  );

  const columns = useMemo<Column<StockGroupTableRow>[]>(
    () => [
      {
        id: 'expander',
        // Chaîne non vide obligatoire : le Table du core ne rend pas de <th> si Header est falsy (''),
        // ce qui décale toutes les colonnes par rapport au corps du tableau.
        Header: '\u00a0',
        disableSortBy: true,
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;

          return (
            <$ExpandCell>
              {row.canExpand ? (
                <$ExpandToggle
                  type="button"
                  aria-expanded={row.isExpanded}
                  aria-label={
                    row.isExpanded ? 'Masquer les pièces du stock' : 'Afficher les pièces du stock'
                  }
                  {...row.getToggleRowExpandedProps()}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      transform: row.isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 150ms ease',
                    }}
                  >
                    <ChevronDownIcon size="16px" />
                  </span>
                </$ExpandToggle>
              ) : (
                <span style={{ width: 24, display: 'inline-block' }} aria-hidden />
              )}
            </$ExpandCell>
          );
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Stock',
        id: 'stock',
        disableSortBy: true,
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;
          const isParentWithSubRows = row.depth === 0 && (row.original.subRows?.length ?? 0) > 0;

          return (
            <$StockParentBand>
              <$StockParentBandStockName $isChildRow={!isParentWithSubRows}>
                {row.original.nomStock}
              </$StockParentBandStockName>
              <$StockParentBandSite>{row.original.siteAssocie}</$StockParentBandSite>
            </$StockParentBand>
          );
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Nom de la pièce',
        accessor: 'nomPiece',
        id: 'nomPiece',
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;
          const isParentWithSubRows = row.depth === 0 && (row.original.subRows?.length ?? 0) > 0;

          if (isParentWithSubRows) {
            return null;
          }

          return <span>{row.original.nomPiece}</span>;
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Réf. constructeur',
        accessor: 'referenceConstructeur',
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Constructeur',
        accessor: 'constructeur',
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Réf. fournisseur',
        accessor: 'referenceFournisseur',
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Fournisseur',
        accessor: 'fournisseur',
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Qté',
        id: 'quantity',
        accessor: (row) => row.quantity,
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;

          return <span style={{ fontWeight: row.depth === 0 ? 600 : 500 }}>{row.original.quantity}</span>;
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Qté disponible',
        id: 'quantiteDisponible',
        accessor: (row) => row.quantiteDisponible ?? -1,
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;

          if (row.original.quantiteDisponible === null) {
            return null;
          }

          return (
            <span style={{ fontWeight: row.depth === 0 ? 600 : 500 }}>{row.original.quantiteDisponible}</span>
          );
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: 'Dernière mise à jour',
        id: 'dateDerniereMiseAJour',
        accessor: (row) => row.dateDerniereMiseAJour,
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;
          const isStockGroupParent = row.depth === 0 && (row.original.subRows?.length ?? 0) > 0;

          return (
            <span
              style={{
                fontWeight: row.depth === 0 ? 600 : 500,
                whiteSpace: 'nowrap',
                ...(isStockGroupParent ? { fontSize: '12px' } : {}),
              }}
            >
              {formatStockDateTime(row.original.dateDerniereMiseAJour)}
            </span>
          );
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: () => (
          <span title="Somme des prix d’achat HT des unités">
            {'\u2211'} Prix achat HT
          </span>
        ),
        id: 'sommePrixAchatHt',
        accessor: (row) => row.sommePrixAchatHt,
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;
          const isStockGroupParent = row.depth === 0 && (row.original.subRows?.length ?? 0) > 0;

          return (
            <span
              style={{
                fontWeight: row.depth === 0 ? 600 : 500,
                ...(isStockGroupParent ? { fontSize: '12px' } : {}),
              }}
            >
              {formatSumEuro(row.original.sommePrixAchatHt)}
            </span>
          );
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        Header: () => (
          <span title="Somme des prix de vente TTC des unités">
            {'\u2211'} Prix vente TTC
          </span>
        ),
        id: 'sommePrixVenteTtc',
        accessor: (row) => row.sommePrixVenteTtc,
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;
          const isStockGroupParent = row.depth === 0 && (row.original.subRows?.length ?? 0) > 0;

          return (
            <span
              style={{
                fontWeight: row.depth === 0 ? 600 : 500,
                ...(isStockGroupParent ? { fontSize: '12px' } : {}),
              }}
            >
              {formatSumEuro(row.original.sommePrixVenteTtc)}
            </span>
          );
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
      {
        // Chaîne non vide : le Table du core ne rend pas de <th> si Header est falsy (''),
        // ce qui décale les colonnes ; pas de libellé visible pour la colonne d’actions.
        Header: '\u00a0',
        id: 'actions',
        disableSortBy: true,
        Cell: (props: CellProps<StockGroupTableRow>) => {
          const row = props.row as StockTableRow;
          const isStockGroupParent = row.depth === 0 && (row.original.subRows?.length ?? 0) > 0;

          if (isStockGroupParent) {
            if (!SHOW_VIEW_BUTTON_ON_STOCK_PARENT_ROW) {
              return null;
            }

            return (
              <$ViewButton
                type="button"
                aria-label={`Voir toutes les pièces du stock ${row.original.nomStock}`}
                onClick={() => handleOpenStockDetail(row.original)}
              >
                Voir
              </$ViewButton>
            );
          }

          return (
            <$ViewButton
              type="button"
              aria-label={`Voir le détail des unités pour ${row.original.referencePieceDetachee}`}
              onClick={() => handleOpenDetail(row.original)}
            >
              Voir
            </$ViewButton>
          );
        },
        getProps: (cell?: Cell<StockGroupTableRow, unknown>) => getStockTableCellProps(cell),
      },
    ],
    [handleOpenDetail, handleOpenStockDetail],
  ) as Column<StockGroupTableRow>[];

  return (
    <div style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <div style={{ marginTop: '2rem' }}>
        <Panel>
            <Heading level={2}>Tableau des pièces en stock</Heading>
            <SearchAndFiltersContainer>
              <StockTableViews
                searchQuery={searchQuery}
                selectedFilters={selectedFilters}
                onApplyView={handleApplyStockTableView}
              />
              <SearchInputWrapper>
                <SearchInput
                  type="text"
                  placeholder="Rechercher une pièce, fournisseur, dossier..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </SearchInputWrapper>
              <FiltersContainer>
                {(
                  [
                    'siteAssocie',
                    'nomStock',
                    'fournisseur',
                    'constructeur',
                    'statutElementStock',
                    'etatPiece',
                  ] as const
                ).map((filterType) => {
                  const isMenuOpen = openFilterMenu === filterType;
                  const isStockFilter = filterType === 'nomStock';
                  const selectedCount = isStockFilter
                    ? isStockFilterPartiallySelected
                      ? selectedFilters.nomStock.length
                      : 0
                    : selectedFilters[filterType].length;
                  const isFilterActive = isStockFilter
                    ? isStockFilterPartiallySelected
                    : selectedCount > 0;
                  const labels: Record<StockFilterKey, string> = {
                    siteAssocie: 'Site',
                    nomStock: 'Stock',
                    fournisseur: 'Fournisseur',
                    constructeur: 'Constructeur',
                    statutElementStock: 'Statut',
                    etatPiece: 'État',
                  };

                  return (
                    <div
                      key={filterType}
                      ref={filterType === openFilterMenu ? filterMenuRef : null}
                      style={{ position: 'relative' }}
                    >
                      <FilterButtonStyled
                        $isActive={isFilterActive || isMenuOpen}
                        onClick={(event) => handleFilterButtonClick(filterType, event)}
                      >
                        <FilterIconWrapper>{getFilterIcon(filterType)}</FilterIconWrapper>
                        <span>{labels[filterType]}</span>
                        {selectedCount > 0 && <FilterNumber>{selectedCount}</FilterNumber>}
                        <FilterChevronWrapper $isMenuOpen={isMenuOpen}>
                          <ChevronDownIcon size="16px" />
                        </FilterChevronWrapper>
                      </FilterButtonStyled>
                      {isMenuOpen && (
                        <FilterMenuDropdown
                          $isOpen={isMenuOpen}
                          $top={filterMenuPosition.top}
                          $left={filterMenuPosition.left}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {isStockFilter && (
                            <FilterMenuItemSelectAll>
                              <input
                                type="checkbox"
                                checked={isStockFilterFullySelected}
                                ref={(element) => {
                                  if (element) {
                                    element.indeterminate = isStockFilterPartiallySelected;
                                  }
                                }}
                                onChange={handleStockFilterSelectAllToggle}
                              />
                              <span>Tout sélectionner</span>
                            </FilterMenuItemSelectAll>
                          )}
                          {filterOptions[filterType].map((option) => {
                            const isSelected = selectedFilters[filterType].includes(option.value);

                            return (
                              <FilterMenuItem key={`${filterType}-${option.value}`}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleFilterToggle(filterType, option.value)}
                                />
                                <span>
                                  {option.label} ({option.count ?? 0})
                                </span>
                              </FilterMenuItem>
                            );
                          })}
                        </FilterMenuDropdown>
                      )}
                    </div>
                  );
                })}
              </FiltersContainer>
              <ActionsContainer>
                <Button
                  type={BUTTON_TYPES.BUTTON}
                  secondary
                  onClick={handleOpenStockBulkDrawer}
                  dataTestId="stock-quick-action-button"
                >
                  Action rapide
                </Button>
                <Button type={BUTTON_TYPES.BUTTON} secondary>
                  Exporter
                </Button>
              </ActionsContainer>
            </SearchAndFiltersContainer>
            <$StockTableScrollArea>
              <Table<StockGroupTableRow>
                columns={columns}
                data={groupedFilteredData}
                plugins={[useSortBy, useExpanded]}
                initialState={{ expanded: {} } as Record<string, unknown>}
                dataTestId="stock-table"
              />
            </$StockTableScrollArea>
          </Panel>
      </div>
      <Drawer
        isOpen={isDetailDrawerOpen}
        onClose={handleCloseDetailDrawer}
        position={DrawerPosition.Right}
        width={1120}
      >
        <Heading level={2}>Détail des unités en stock</Heading>
        {detailDrawerHeader && (
          <>
            {detailDrawerHeader.referencePieceDetachee !== null ? (
              <Paragraph style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                <strong>Ref. pièce détachée :</strong> {detailDrawerHeader.referencePieceDetachee}
              </Paragraph>
            ) : (
              <Paragraph style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                <strong>Contenu :</strong> toutes les pièces du stock (toutes références)
              </Paragraph>
            )}
            <Paragraph style={{ marginTop: '0.35rem', marginBottom: 0 }}>
              <strong>Stock :</strong> {detailDrawerHeader.nomStock}
            </Paragraph>
            <Paragraph style={{ marginTop: '0.35rem', marginBottom: 0 }}>
              <strong>Nombre d&apos;unités :</strong> {detailDrawerUnits.length}
            </Paragraph>
          </>
        )}
        <Paragraph style={{ marginTop: '0.75rem', marginBottom: 0, fontSize: '0.875rem', color: '#64748b' }}>
          Nom de la pièce, état, statut, emplacement, dates, prix et référence dossier pour chaque unité physique.
        </Paragraph>
        {detailDrawerUnits.length > 0 && (
          <$DetailTable>
            <thead>
              <tr>
                <th>Réf. fournisseur</th>
                <th>Ref. pièce détachée</th>
                <th>Nom de la pièce</th>
                <th>État</th>
                <th>Statut</th>
                <th>Emplacement</th>
                <th>Date d&apos;entrée en stock</th>
                <th>Prix achat HT</th>
                <th>Prix vente TTC</th>
                <th>Réf. dossier</th>
              </tr>
            </thead>
            <tbody>
              {detailDrawerUnits.map((unit) => (
                <tr key={unit.idElementStock}>
                  <td>{unit.referenceFournisseur}</td>
                  <td>{unit.referencePieceDetachee}</td>
                  <td>{unit.nomPiece}</td>
                  <td>{unit.etatPiece}</td>
                  <td>
                    <Tag
                      success={unit.statutElementStock === 'Réservé'}
                      alert={
                        unit.statutElementStock === 'En attente' ||
                        unit.statutElementStock === STOCK_STATUT_ANOMALIE.casseeIntervention ||
                        unit.statutElementStock === STOCK_STATUT_ANOMALIE.casseeReception ||
                        unit.statutElementStock === STOCK_STATUT_ANOMALIE.defectueuse ||
                        unit.statutElementStock === STOCK_STATUT_ANOMALIE.manquante
                      }
                      neutral={
                        unit.statutElementStock !== 'Réservé' &&
                        unit.statutElementStock !== 'En attente' &&
                        unit.statutElementStock !== STOCK_STATUT_ANOMALIE.casseeIntervention &&
                        unit.statutElementStock !== STOCK_STATUT_ANOMALIE.casseeReception &&
                        unit.statutElementStock !== STOCK_STATUT_ANOMALIE.defectueuse &&
                        unit.statutElementStock !== STOCK_STATUT_ANOMALIE.manquante
                      }
                      withoutMargin
                      isSmall
                    >
                      {unit.statutElementStock}
                    </Tag>
                  </td>
                  <td>{unit.emplacementStock === '-' ? '-' : unit.emplacementStock}</td>
                  <td>{formatStockDate(unit.dateEntreeStock)}</td>
                  <td>{unit.prixAchatHt}</td>
                  <td>{unit.prixVenteTtc}</td>
                  <td>{unit.referenceDossier === '-' ? '-' : unit.referenceDossier}</td>
                </tr>
              ))}
            </tbody>
          </$DetailTable>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
          <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleCloseDetailDrawer}>
            Fermer
          </Button>
        </div>
      </Drawer>
      <StockBulkActionDrawer
        isOpen={isStockBulkDrawerOpen}
        onClose={handleCloseStockBulkDrawer}
        availableStocks={availableStocksForDrawer}
        onConfirmInventory={handleConfirmInventoryBulk}
        onConfirmAddParts={handleConfirmAddParts}
      />
    </div>
  );
};

export default StockPage;
